import React from 'react';
import { Trash2, Lock, GraduationCap, Archive, RotateCcw } from 'lucide-react';

// Get styling based on certificate source and status
const getCertificateStyle = (cert) => {
  if (cert.status === 'archived') {
    return 'bg-gray-100 border border-gray-300 opacity-70';
  }
  if (cert.source === 'training') {
    return 'bg-blue-50 border border-blue-200';
  }
  return 'bg-gray-50 border border-gray-200';
};

// Get badge for certificate
const getCertificateBadge = (cert) => {
  if (cert.status === 'archived') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-gray-600 bg-gray-200 rounded">
        <Archive className="h-3 w-3" />
        Archived
      </span>
    );
  }
  if (cert.source === 'training') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-blue-700 bg-blue-100 rounded">
        <GraduationCap className="h-3 w-3" />
        Training
      </span>
    );
  }
  return null;
};

const CertificatesList = ({
  certificates,
  onRemoveCertificate,
  onArchiveCertificate,
  onUnarchiveCertificate
}) => {
  if (!certificates || certificates.length === 0) {
    return null;
  }

  // Separate active and archived certificates
  const activeCerts = certificates.filter(c => c.status !== 'archived');
  const archivedCerts = certificates.filter(c => c.status === 'archived');

  const formatDate = (dateValue) => {
    if (!dateValue) return 'No expiry';
    try {
      const date = new Date(dateValue);
      if (isNaN(date.getTime())) return 'Invalid date';
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
      return 'Invalid date';
    }
  };

  const renderCertificate = (cert) => (
    <div key={cert.id} className={`flex items-start justify-between rounded-lg p-3 ${getCertificateStyle(cert)}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className={`text-sm font-medium ${cert.status === 'archived' ? 'text-gray-500 line-through' : 'text-gray-900'}`}>
            {cert.type}
          </p>
          {getCertificateBadge(cert)}
          {cert.isReadOnly && cert.status !== 'archived' && (
            <Lock className="h-3.5 w-3.5 text-gray-400" title="Read-only (issued via training)" />
          )}
        </div>
        <p className="text-xs text-gray-500 mt-1">
          {cert.number && `No: ${cert.number} • `}
          Expires: {formatDate(cert.expiry_date)}
        </p>
        {cert.status === 'archived' && cert.reason && (
          <p className="text-xs text-gray-600 mt-1">
            <span className="font-medium">Reason:</span> {cert.reason}
          </p>
        )}
        {cert.status === 'archived' && cert.archivedAt && (
          <p className="text-xs text-gray-400 mt-0.5">
            Archived: {formatDate(cert.archivedAt)}
            {cert.archivedBy && ` by ${cert.archivedBy}`}
          </p>
        )}
      </div>

      {/* Action buttons */}
      <div className="flex items-center gap-1 ml-2">
        {cert.status === 'archived' && onUnarchiveCertificate && (
          <button
            type="button"
            onClick={() => onUnarchiveCertificate(cert)}
            className="p-1.5 text-green-600 hover:text-green-700 hover:bg-green-50 rounded"
            title="Restore certificate"
          >
            <RotateCcw className="h-4 w-4" />
          </button>
        )}
        {cert.status !== 'archived' && onArchiveCertificate && (
          <button
            type="button"
            onClick={() => onArchiveCertificate(cert)}
            className="p-1.5 text-amber-600 hover:text-amber-700 hover:bg-amber-50 rounded"
            title="Archive certificate"
          >
            <Archive className="h-4 w-4" />
          </button>
        )}
        {!cert.isReadOnly && cert.status !== 'archived' && onRemoveCertificate && (
          <button
            type="button"
            onClick={() => onRemoveCertificate(cert.id)}
            className="p-1.5 text-red-600 hover:text-red-700 hover:bg-red-50 rounded"
            title="Delete certificate"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Active Certificates */}
      {activeCerts.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-gray-900">Current Certificates</h4>
          {activeCerts.map(renderCertificate)}
        </div>
      )}

      {/* Archived Certificates */}
      {archivedCerts.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-gray-500 flex items-center gap-2">
            <Archive className="h-4 w-4" />
            Archived Certificates ({archivedCerts.length})
          </h4>
          {archivedCerts.map(renderCertificate)}
        </div>
      )}
    </div>
  );
};

export default CertificatesList;
